/* @flow */

import { type AxiosPromise } from 'axios';
import xhr from './xhr';
import * as apiIugu from './api-iugu';
import {
    type Produto,
    type ProdutoPedido,
    type Categoria,
    type Estoque,
} from '../produtos/types';
import { type Funcionamento } from '../funcionamento/types';
import { type GrupoComplemento } from '../complementos/types';
import { type FormaPagamento } from '../formas-pagamento/types';
import { type Plano } from '../institucional/types';
import {
    type Pedido,
    type TotaisEtapasRetornoApi,
    type IntervaloData,
    type EtapasId,
} from '../pedidos/types';
import {
    type Empresa,
    type UnidadesNegocio,
    type UrlLojas,
    type ConfiguracoesPendentes,
    type Recurso,
    type Moeda,
} from '../empresa/types';
import { type EnderecoEntrega, type Endereco } from '../enderecos/types';
import {
    type Senhas,
    type Usuario,
    type Contato,
    type ExtratoMoedaComSaldo,
} from '../usuario/types';
import { type Impressao } from '../impressao/types';
import { type Entregadores } from '../entregadores/types';
import { type Mensalidade, type Totais } from '../mensalidade/types';
import { type ServicoPagamento } from '../empresa/historico-financeiro/types';
import dataFinalEInicialMesAtual from './data-inicial-e-final-mes-atual';

type GetCategoriasParams = {
    quantidade: number,
    ativo?: 0 | 1,
    funcionamento?: 0 | 1,
    embed?: string,
    colunas?: string[],
}

type Void = AxiosPromise<void>;
type ComId = AxiosPromise<{ id: number }>;

const editarMensalidade = (mensalidadeId: number, params: Object): Void => (
    xhr.put(`/empresas/mensalidade/${mensalidadeId}`, params)
);

const getCategorias = (params: GetCategoriasParams): AxiosPromise<Categoria[]> => (
    xhr.get('/v2/categorias', { params })
);

const getCategoriaPorId = (id: number): AxiosPromise<Categoria> => (
    xhr.get(`/v2/categorias/${id}`)
);

const criarCategoria = (categoria: Object): ComId => (
    xhr.post('/v2/categorias', categoria)
);

const getFuncionamento = (): AxiosPromise<Funcionamento[]> => (
    xhr.get('/funcionamento')
);

const criarProduto = (produto: Object): ComId => (
    xhr.post('/produtos', produto)
);
const editarProduto = (id: number, produto: Object): Void => (
    xhr.put(`/produtos/${id}`, produto)
);

const editarProdutos = (produtos: Object[]): Void => (
    xhr.put('/produtos', { produtos })
);

const cadastrarProduto = (produto: Object): ComId => (
    xhr.post('/produtos', produto)
);

const deletarProduto = (id: number): Void => (
    xhr.delete(`/produtos/${id}`)
);

const deletarProdutoLixeira = (id: number): Void => (
    xhr.delete(`/lixeira-produtos/${id}`)
);

const editarProdutoLixeira = (id: number, produto: Object): Void => (
    xhr.put(`/lixeira-produtos/${id}`, produto)
);

const getProdutosPorId = (produtosId: number): AxiosPromise<Produto> => (
    xhr.get(`/produtos/${produtosId}`)
);

const getProdutos = (
    params: { quantidade: number, ativo?: number },
): AxiosPromise<{data: Produto[]}> => xhr.get('/produtos', { params });

const deletarImagemPorProdutosId = (produtosId: number): Void => (
    xhr.delete(`/produtos/${produtosId}/imagem`)
);

const editarUsuario = (usuario: Object): Void => (
    xhr.put(`/usuarios/${usuario.id}`, usuario)
);

const getGruposComplemento = (
    params: {
        quantidade: number,
        ativo?: number,
        ordenacao?: string,
        embed?: 'complementos',
    },
): AxiosPromise<{ data: GrupoComplemento[] }> => (
    xhr.get('/grupos-complemento', { params })
);

const removerProdutoDoGrupoComplemento = ({
    complementosGrupoId,
    produtosId,
}: {
    complementosGrupoId: number,
    produtosId: number,
}): Void => (
    xhr.delete(`/grupos-complemento/${complementosGrupoId}/produtos/${produtosId}`)
);

const editarProdutoDoGrupoComplemento = (
    {
        complementosGrupoId,
        produtosId,
    }: {
        complementosGrupoId: number,
        produtosId: number,
    },
    data: Object,
): Void => (
    xhr.put(`/grupos-complemento/${complementosGrupoId}/produtos/${produtosId}`, data)
);

const adicionarProdutoAoGrupoComplemento = ({
    produtosId,
    preco,
    complementosGrupoId,
}: {
    produtosId: number,
    preco: number,
    complementosGrupoId: number,
}): ComId => (
    xhr.post(`/grupos-complemento/${complementosGrupoId}/produtos`, {
        produtosId,
        preco,
    })
);

const getUsuario = (): AxiosPromise<Usuario> => xhr.get('/usuario');

const getUsuarios = (params: Object): AxiosPromise<{ data: Usuario[] }> => xhr.get('/usuarios', { params });

const getUsuarioPorId = (usuariosId: number): AxiosPromise<Usuario> => (
    xhr.get(`/usuarios/${usuariosId}`)
);

const editarUsuarioPorId = (usuariosId: number, data: Object): AxiosPromise<{}> => (
    xhr.put(`/usuarios/${usuariosId}`, data)
);

const criarUsuario = (data: Object): ComId => (
    xhr.post('/usuarios', data)
);

const getMensalidade = (): AxiosPromise<Mensalidade[]> => xhr.get('/empresas/mensalidade');

const getMensalidadesPorEmpresaId = (
    empresasId: number,
    params: Object,
): AxiosPromise<Mensalidade[]> => (
    xhr.get(`/empresas/mensalidade/${empresasId}`, { params })
);

const criarPedido = (pedido: Object): AxiosPromise<{id: number}> => (
    xhr.post('/pedidos', pedido)
);

const editarPedido = (id: number, pedido: Object): Void => (
    xhr.put(`/pedidos/${id}`, pedido)
);

const getPedidoPorId = (id: number): AxiosPromise<Pedido> => (
    xhr.get(`/pedidos/${id}`)
);

const getPedidosParaAtendimentoPorId = (id: number): AxiosPromise<Pedido> => (
    xhr.get(`/pedidos/pedido-atendimento/${id}`)
);

const getPedidos = (params: Object): AxiosPromise<{ data: Pedido[] }> => xhr.get('/pedidos', { params });

export type Poll = {
    id: number,
    pedidosId: number,
    etapasId: EtapasId,
    entrada: string,
};

const polling = (params: Object): AxiosPromise<Poll[]> => (
    xhr.get('/eventos/polling', { params })
);

const reconhecimento = (ids: number[]): Void => xhr.post('/eventos/reconhecimento', ids);

const getTotaisPorEtapa = (intervalo: IntervaloData): AxiosPromise<TotaisEtapasRetornoApi[]> => (
    xhr.post('/pedidos/totais-por-etapa', intervalo)
);

const adicionarProdutoAoPedido = (pedidosId: number, produto: Object): Void => (
    xhr.post(`/pedidos/${pedidosId}/produtos`, produto)
);

const getProdutosPorPedidosId = (pedidosId: number): AxiosPromise<ProdutoPedido[]> => (
    xhr.get(`/pedidos/${pedidosId}/produtos`)
);

const editarProdutoDoPedido = (
    pedidosId: number,
    produtosId: number,
    produto: Object,
): Void => (
    xhr.put(`/pedidos/${pedidosId}/produtos/${produtosId}`, produto)
);

const deletarProdutoDoPedido = (
    pedidosId: number,
    produtosId: number,
): Void => (
    xhr.delete(`/pedidos/${pedidosId}/produtos/${produtosId}`)
);

const criarEmpresa = (empresa: Empresa): ComId => xhr.post(
    '/empresas',
    empresa,
);

const getUrlsLojas = (empresasId: number): AxiosPromise<UrlLojas> => xhr.get(
    `/empresas/${empresasId}/urls-lojas`,
);

const getRecursosPorEmpresasId = (empresasId: number): AxiosPromise<Recurso[]> => xhr.get(
    `/empresas/${empresasId}/recursos`,
);

const getRecursosEmpresa = (): AxiosPromise<Recurso[]> => xhr.get(
    '/empresa/recursos',
);

const cadastrarRecursosPorEmpresasId = (
    empresasId: number,
    recursos: Recurso[],
): Void => xhr.post(
    `/empresas/${empresasId}/recursos`,
    recursos,
);

type ParamsGetEmpresa = {
    tokenTransacao?: string,
};

const getEmpresas = (params: ParamsGetEmpresa): AxiosPromise<{ data: Empresa[] }> => (
    xhr.get('/empresas', { params })
);

const getEstabelecimentoPorIdentificador = (identifier: string): AxiosPromise<Empresa[]> => (
    xhr.get('/empresas/packages', { params: { identificador: identifier } })
);

const getEstabelecimentoPorUrlDownload = (urlDownload: string): AxiosPromise<UrlLojas[]> => (
    xhr.get(`/empresas/urls-lojas/${urlDownload}`)
);

const getFormasPagamento = (): AxiosPromise<FormaPagamento[]> => (
    xhr.get('/empresa/formas-pagamento')
);

const getImpressao = (): AxiosPromise<Impressao> => (
    xhr.get('/empresa/impressao')
);

const getPlanos = (): AxiosPromise<Plano[]> => xhr.get('/planos');

const contratacao = (dados: Object): AxiosPromise<{pagamentoUrl: string, id: number}> => (
    xhr.post('/contratacao', dados)
);

const getSessaoPagSeguroMaxscalla = (): AxiosPromise<{ sessao: string, script: string }> => (
    xhr.get('/pagseguro/sessao/maxscalla')
);

const getEnderecoEntrega = (
    usuariosId: number,
    params: Object,
): AxiosPromise<EnderecoEntrega[]> => (
    xhr.get(`/usuarios/${usuariosId}/enderecos-entrega`, { params })
);

const getEnderecosPorUsuariosId = (usuariosId: number): AxiosPromise<Endereco[]> => (
    xhr.get(`/usuarios/${usuariosId}/enderecos`)
);

const alterarSenha = (senhas: Senhas): Void => (
    xhr.post('/usuario/alterar-senha', senhas)
);

const getEntregadores = (params: Object): AxiosPromise<Entregadores[]> => (
    xhr.get('/entregadores', { params })
);

const getContatosPorUsuariosId = (usuariosId: number): AxiosPromise<Contato[]> => (
    xhr.get(`/usuarios/${usuariosId}/contatos`)
);

type Loggi = {
    authorization: string,
};

const getLoggi = (): AxiosPromise<Loggi> => (
    xhr.get('/empresa/loggi')
);

const cadastrarLoggi = (email: string, password: string): AxiosPromise<Void> => (
    xhr.post('/empresa/loggi', { email, password })
);

type TiposEmpresa = {
    id: number,
    nome: string,
    valor: string,
}

const getTiposEmpresa = (): AxiosPromise<TiposEmpresa[]> => xhr.get('/tipos-empresa');
const getEmpresa = (): AxiosPromise<Empresa> => xhr.get('/empresa');
const editarEmpresa = (dados: Object): AxiosPromise<Empresa> => xhr.put('/empresa', dados);
const getEnderecoEmpresa = (): AxiosPromise<Endereco> => xhr.get('/empresa/enderecos');
const getUrlLojaEmpresa = (): AxiosPromise<UrlLojas> => xhr.get('/empresa/urls-lojas');

const getConfiguracaoPendentes = (): AxiosPromise<ConfiguracoesPendentes> => (
    xhr.get('/relatorios/aplicativo/configuracao-pendente'));

const getMoeda = (): AxiosPromise<Moeda> => (
    xhr.get('/empresa/moeda')
);

const criarMoeda = (moeda: Moeda): Void => (
    xhr.post('/empresa/moeda', moeda)
);

const getExtratoMoedasUsuario = (
    usuariosId: number,
    params: IntervaloData,
): AxiosPromise<ExtratoMoedaComSaldo[]> => (
    xhr.get(`/usuarios/${usuariosId}/extrato-moedas`, { params })
);

const cadastrarTokenPagamento = (
    empresasId: number,
    token: { tokenPagarme: string },
): Void => (
    xhr.put(`empresas/${empresasId}`, token)
);

const getExtratosPorEmpresa = (empresaId: number, params: Object): AxiosPromise<Object> => (
    xhr.get(`/iugu/extrato/${empresaId}`, { params })
);

const cadastrarMeusCartoes = (params: Object): Void => xhr.post('/iugu/meus-cartoes', params);

const listarCartoesCadastrados = async (): Object => {
    const { data } = await xhr.get('/iugu/meus-cartoes');
    return data;
};

const excluirCartaoCadastrado = (formaPagamentoId: number): AxiosPromise<Object> => xhr.delete(`/iugu/meus-cartoes/${formaPagamentoId}`);
const editarClienteIugu = (empresaId, params) => xhr.put(`/iugu/meus-cartoes/${empresaId}`, params);

const minhaAssinatura = (empresaId: string): AxiosPromise<Object> => xhr.get(`/assinatura/${empresaId}`);

const listarAssinantes = (): AxiosPromise<Object> => xhr.get('/iugu/clientes');

const getAssinaturaProCustomerId = (customerId: string): AxiosPromise<Object> => xhr.get(`/iugu/clientes/${customerId}`);

const getFaturasProCustomerId = (customerId: string): AxiosPromise<Object> => xhr.get(`/iugu/faturas/${customerId}`);

const getDadosCobranca = (empresaId: number): AxiosPromise<Object> => (
    xhr.get(`empresas/cobranca/${empresaId}`)
);

const updateEmpresa = (empresaId: number, dados: Object): Void => xhr.put(`/empresas/${empresaId}`, dados);

const enviarNotificacaoPush = (notificacao: Object): AxiosPromise<Object> => (
    xhr.post(`/notificacoes/${notificacao.id}/send`)
);

const getLogNotificacaoPush = (notificacaoId: number): AxiosPromise<Object> => (
    xhr.get(`/notificacoes/${notificacaoId}/send`)
);

const getUnidadesNegocio = (): AxiosPromise<UnidadesNegocio[]> => (
    xhr.get('/empresa/unidades-negocio')
);

const getMensalidadesPorEmpresa = (params: Object): AxiosPromise<ServicoPagamento[]> => (
    xhr.get('/empresas/servico-pagamento-mensal', { params })
);

const postMensalidadesPorEmpresa = (dados: Object): AxiosPromise<ServicoPagamento[]> => (
    xhr.post('/empresas/servico-pagamento-mensal', dados)
);

const buscarMensalidadesPorEmpresaId = (
    empresasId: number,
    params: Object,
): AxiosPromise<ServicoPagamento[]> => (
    xhr.get(`/empresas/servico-pagamento-mensal/${empresasId}`, { params })
);

const buscarUltimaMensalidade = (): AxiosPromise<ServicoPagamento[]> => {
    const params = { ...dataFinalEInicialMesAtual() };

    return (
        xhr.get('/empresas/status-mensalidade', { params })
    );
};

const getResumoFaturamentoTotais = (): AxiosPromise<Totais[]> => (
    xhr.get('/relatorios/resumo-faturamento/0')
);

const getHistoricoNoEstoque = (produtosId: number): AxiosPromise<Estoque[]> => (
    xhr.get(`/estoque/${produtosId}`)
);

const setQtdeProdutoNoEstoque = (params: Object): AxiosPromise<Object> => (
    xhr.post('/estoque', params)
);

const getTotaisEstoque = (params: { quantidade: number, ativo?: number }): AxiosPromise<Object> => (
    xhr.get('/totalizador-estoque', { params })
);

const getCoresPorEmpresasId = (empresasId: number): AxiosPromise<Cores> => xhr.get(`/empresas/${empresasId}/cores`);

const api = {
    editarMensalidade,
    criarEmpresa,
    criarProduto,
    deletarImagemPorProdutosId,
    editarProduto,
    editarProdutos,
    deletarProduto,
    deletarProdutoLixeira,
    editarProdutoLixeira,
    getFuncionamento,
    cadastrarProduto,
    editarUsuario,
    getProdutosPorId,
    getProdutos,
    getCategorias,
    getCategoriaPorId,
    criarCategoria,
    getGruposComplemento,
    getUsuario,
    getUsuarios,
    getUsuarioPorId,
    criarUsuario,
    editarUsuarioPorId,
    getPedidos,
    getTotaisPorEtapa,
    criarPedido,
    editarPedido,
    adicionarProdutoAoPedido,
    editarProdutoDoPedido,
    deletarProdutoDoPedido,
    getFormasPagamento,
    getPlanos,
    contratacao,
    getSessaoPagSeguroMaxscalla,
    getEnderecoEntrega,
    getEmpresas,
    getEstabelecimentoPorIdentificador,
    getEstabelecimentoPorUrlDownload,
    alterarSenha,
    getTiposEmpresa,
    getPedidoPorId,
    getPedidosParaAtendimentoPorId,
    getProdutosPorPedidosId,
    removerProdutoDoGrupoComplemento,
    editarProdutoDoGrupoComplemento,
    getEnderecosPorUsuariosId,
    adicionarProdutoAoGrupoComplemento,
    polling,
    reconhecimento,
    getImpressao,
    getEntregadores,
    getContatosPorUsuariosId,
    getUrlsLojas,
    getConfiguracaoPendentes,
    getUrlLojaEmpresa,
    getEmpresa,
    editarEmpresa,
    getEnderecoEmpresa,
    cadastrarRecursosPorEmpresasId,
    getRecursosPorEmpresasId,
    getRecursosEmpresa,
    getLoggi,
    cadastrarLoggi,
    criarMoeda,
    getMoeda,
    getExtratoMoedasUsuario,
    cadastrarTokenPagamento,
    getMensalidade,
    getMensalidadesPorEmpresaId,
    getExtratosPorEmpresa,
    updateEmpresa,
    getDadosCobranca,
    enviarNotificacaoPush,
    getLogNotificacaoPush,
    getUnidadesNegocio,
    getMensalidadesPorEmpresa,
    postMensalidadesPorEmpresa,
    buscarMensalidadesPorEmpresaId,
    buscarUltimaMensalidade,
    getResumoFaturamentoTotais,
    getHistoricoNoEstoque,
    setQtdeProdutoNoEstoque,
    getTotaisEstoque,
    cadastrarMeusCartoes,
    listarCartoesCadastrados,
    excluirCartaoCadastrado,
    editarClienteIugu,
    minhaAssinatura,
    listarAssinantes,
    getAssinaturaProCustomerId,
    getFaturasProCustomerId,
    getCoresPorEmpresasId,
    ...apiIugu,
};

export default api;
